import { Button, Divider, Form, Grid, Input, InputNumber, Message, Radio, Space, Table } from "@arco-design/web-react";
import { IconCopy, IconImport } from "@arco-design/web-react/icon";
import React, { useEffect, useState, useCallback } from "react";
import { usePasswordCheck } from "../hooks/usePasswordCheck";
import { copy } from "../utils/copy";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const RadioGroup = Radio.Group;

const EXPORT_KEY = "tangent_to_dresser_export";
const EXPORT_READY_KEY = "tangent_to_dresser_ready";
const EXPORT_START_ROW_KEY = "tangent_to_dresser_start_row";

const TangentCalculation = ({ onPasswordInvalid }) => {
  const { checkPasswordBeforeAction } = usePasswordCheck(onPasswordInvalid);
  const [form] = Form.useForm();
  const [calculationType, setCalculationType] = useState("1");
  const [results, setResults] = useState({});
  const [pointData, setPointData] = useState([]);
  const [btnEnabled, setBtnEnabled] = useState(false);
  const [exportStartRow, setExportStartRow] = useState(2);

  const STORAGE_KEY = "tangent_values_v1";

  const loadData = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  };

  const saveData = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  };

  const persist = () => {
    const data = {
      calculationType,
      formValues: form.getFieldsValue?.() || {}
    };
    saveData(data);
  };

  const checkFormValid = useCallback((values, type) => {
    const { R3, angle1, R2, phiLeft, phiRight, leftLength } = values;
    const calcType = type || calculationType;

    let isValid = false;
    if (calcType === "1") {
      isValid = R3 > 0 && angle1 > 0 && R2 > 0 && phiLeft > 0 && phiRight > 0 && leftLength > 0;
    } else if (calcType === "2") {
      isValid = R3 > 0 && angle1 > 0 && phiLeft > 0 && phiRight > 0 && leftLength > 0;
    } else if (calcType === "3") {
      isValid = angle1 > 0 && R2 > 0 && phiLeft > 0 && phiRight > 0 && leftLength > 0;
    }

    setBtnEnabled(isValid);
  }, [calculationType]);

  useEffect(() => {
    const saved = loadData();
    if (saved.calculationType) {
      setCalculationType(saved.calculationType);
    }
    if (saved.formValues) {
      form.setFieldsValue(saved.formValues);
      checkFormValid(saved.formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentValues = form.getFieldsValue();
    checkFormValid(currentValues, calculationType);
  }, [calculationType, checkFormValid, form]);

  const toFixed4 = (num) => {
    if (typeof num !== 'number' || isNaN(num) || !isFinite(num)) {
      return "非法条件";
    }
    return Number(num.toFixed(4));
  };

  const onCalculate = () => {
    checkPasswordBeforeAction(() => {
      const { R3, angle1, R2, phiLeft, phiRight, leftLength } = form.getFieldsValue();

      const r3 = calculationType === "3" ? 0 : (Number(R3) || 0);
      const r2 = calculationType === "2" ? 0 : (Number(R2) || 0);
      const a1 = Number(angle1) || 0;
      const pLeft = Number(phiLeft) || 0;
      const pRight = Number(phiRight) || 0;
      const lLeft = Number(leftLength) || 0;

      const angleRad = (a1 * Math.PI) / 180;

      const X3 = r3 > 0 ? 2 * (r3 - r3 * Math.cos(angleRad)) : 0;
      const Z3 = r3 > 0 ? r3 * Math.sin(angleRad) : 0;

      const X2 = r2 > 0 ? 2 * (r2 - r2 * Math.cos(angleRad)) : 0;
      const Z2 = r2 > 0 ? r2 * Math.sin(angleRad) : 0;

      const X1 = pLeft - pRight - X3 - X2;
      const Z1 = a1 > 0 ? X1 / 2 / Math.tan(angleRad) : 0;

      const XB1 = pRight;
      const XB2 = XB1 + X2;
      const XB4 = pLeft;
      const XB3 = XB4 - X3;

      const ZB4 = lLeft;
      const ZB3 = ZB4 + Z3;
      const ZB2 = ZB3 + Z1;
      const ZB1 = ZB2 + Z2;

      const calculatedResults = {
        R3: toFixed4(r3),
        X3: toFixed4(X3),
        Z3: toFixed4(Z3),
        angle1: toFixed4(a1),
        X1: toFixed4(X1),
        Z1: toFixed4(Z1),
        R2: toFixed4(r2),
        X2: toFixed4(X2),
        Z2: toFixed4(Z2)
      };

      setResults(calculatedResults);

      const points = [];
      if (calculationType === "2") {
        points.push({
          key: 'B2',
          point: 'B2',
          X: toFixed4(XB2),
          Z: toFixed4(ZB2),
          R: ''
        });
      } else {
        points.push({
          key: 'B1',
          point: 'B1',
          X: toFixed4(XB1),
          Z: toFixed4(ZB1),
          R: ''
        });
        points.push({
          key: 'B2',
          point: 'B2',
          X: toFixed4(XB2),
          Z: toFixed4(ZB2),
          R: `R${toFixed4(r2)}`
        });
      }
      if (calculationType !== "3") {
        if (calculationType === "2") {
          points.push({
            key: 'B3',
            point: 'B3',
            X: toFixed4(XB3),
            Z: toFixed4(ZB3),
            R: ''
          });
        } else {
          points.push({
            key: 'B3',
            point: 'B3',
            X: toFixed4(XB3),
            Z: toFixed4(ZB3),
            R: ''
          });
        }
        points.push({
          key: 'B4',
          point: 'B4',
          X: toFixed4(XB4),
          Z: toFixed4(ZB4),
          R: `R${toFixed4(r3)}`
        });
      } else {
        points.push({
          key: 'B3',
          point: 'B3',
          X: toFixed4(XB3),
          Z: toFixed4(ZB3),
          R: ''
        });
      }

      setPointData(points);
      persist();
    });
  };

  const onReset = () => {
    form.resetFields();
    setResults({});
    setPointData([]);
    setBtnEnabled(false);
    saveData({ calculationType, formValues: {} });
  };

  const onTypeChange = (value) => {
    const currentValues = form.getFieldsValue();
    setCalculationType(value);
    // 保留公共字段的值，不重置表单
    setResults({});
    setPointData([]);
    // 重新检查表单有效性，传入新的类型
    checkFormValid(currentValues, value);
    saveData({ calculationType: value, formValues: currentValues });
  };

  // 解析 R 值字符串，如 "R2.5" -> 2.5，"" -> 0
  const parseRValue = (rStr) => {
    if (!rStr || typeof rStr !== 'string') return 0;
    const match = rStr.match(/R?([\d.]+)/i);
    return match ? parseFloat(match[1]) || 0 : 0;
  };

  // 导出点位数据到修砂轮程序生成器
  const onExportToDresser = () => {
    if (pointData.length === 0) {
      Message.warning("没有点位数据可导出");
      return;
    }

    // 将点位数据转换为 DresserProgramGenerator 需要的格式
    // pointData: [{point, X, Z, R}, ...] -> rows: [{func, phi, len, r}, ...]
    const exportData = pointData.map((p) => {
      const rValue = parseRValue(p.R);
      const xValue = typeof p.X === 'number' ? p.X : (typeof p.X === 'string' && p.X !== '非法条件' ? parseFloat(p.X) : 0);
      const zValue = typeof p.Z === 'number' ? p.Z : (typeof p.Z === 'string' && p.Z !== '非法条件' ? parseFloat(p.Z) : 0);

      // 根据点位名称判断G代码：B2对应R2用G02，B4对应R3用G03
      let func = 'G01';
      if (rValue > 0) {
        if (p.point === 'B2') {
          func = 'G02'; // R2圆弧
        } else if (p.point === 'B4') {
          func = 'G03'; // R3圆弧
        } else {
          func = 'G02'; // 默认用G02
        }
      }

      return {
        func,
        phi: isNaN(xValue) ? 0 : xValue,
        len: isNaN(zValue) ? 0 : zValue,
        r: rValue,
      };
    });

    localStorage.setItem(EXPORT_KEY, JSON.stringify(exportData));
    localStorage.setItem(EXPORT_READY_KEY, 'true');
    localStorage.setItem(EXPORT_START_ROW_KEY, String(exportStartRow));
    Message.success(`点位数据已准备就绪，将从A${exportStartRow}开始导入`);
  };

  const columns = [
    {
      title: '点位',
      dataIndex: 'point',
      width: 100
    },
    {
      title: 'X(直径)',
      dataIndex: 'X',
      width: 150
    },
    {
      title: 'Z(长度)',
      dataIndex: 'Z',
      width: 150
    },
    {
      title: '',
      dataIndex: 'R',
      width: 100
    }
  ];

  const renderInputForm = () => (
    <Form
      form={form}
      autoComplete="off"
      colon=":"
      layout="vertical"
      onValuesChange={(_, values) => {
        checkFormValid(values);
        persist();
      }}
      style={{ width: '100%', maxWidth: 600 }}
    >
      <FormItem
        label="相切计算类型"
        style={{ marginBottom: 20 }}
      >
        <RadioGroup value={calculationType} onChange={onTypeChange} direction="vertical">
          <Radio value="1" style={{ marginBottom: 8 }}>左圆弧R3、中角度∠1、右圆弧R2相切</Radio>
          <Radio value="2" style={{ marginBottom: 8 }}>左圆弧R3、右角度∠1相切</Radio>
          <Radio value="3">左角度∠1、右圆弧R2相切</Radio>
        </RadioGroup>
      </FormItem>

      <Divider />

      {calculationType !== "3" && (
        <FormItem
          label="R3"
          field="R3"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber
            min={0}
            size="large"
            placeholder="请输入左圆弧大小"
            style={{ width: '100%' }}
          />
        </FormItem>
      )}

      <FormItem
        label="∠1"
        field="angle1"
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber
          min={0}
          size="large"
          placeholder="请输入角度值"
          style={{ width: '100%' }}
        />
      </FormItem>

      {calculationType !== "2" && (
        <FormItem
          label="R2"
          field="R2"
          rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
        >
          <InputNumber
            min={0}
            size="large"
            placeholder="请输入右圆弧大小"
            style={{ width: '100%' }}
          />
        </FormItem>
      )}

      <FormItem
        label="Φ左大"
        field="phiLeft"
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber
          min={0}
          size="large"
          placeholder="请输入左直径"
          style={{ width: '100%' }}
        />
      </FormItem>

      <FormItem
        label="Φ右小"
        field="phiRight"
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber
          min={0}
          size="large"
          placeholder="请输入右直径"
          style={{ width: '100%' }}
        />
      </FormItem>

      <FormItem
        label="L左"
        field="leftLength"
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber
          min={0}
          size="large"
          placeholder="请输入左长度"
          style={{ width: '100%' }}
        />
      </FormItem>

      <FormItem>
        <Space size="medium" wrap>
          <Button type="primary" onClick={onCalculate} disabled={!btnEnabled} size="large" style={{ minWidth: 120 }}>
            立即计算
          </Button>
          <Button onClick={onReset} disabled={!btnEnabled} size="large" style={{ minWidth: 120 }}>
            重置
          </Button>
        </Space>
      </FormItem>
    </Form>
  );

  const renderOutputForm = () => (
    <div style={{ width: '100%', maxWidth: 600 }}>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 15 }}>计算结果</h3>

        {calculationType !== "3" && (
          <Row gutter={[12, 12]} style={{ marginBottom: 10 }}>
            <Col xs={8} sm={8}>
              <FormItem label="R" style={{ marginBottom: 0 }}>
                <Input
                  id="result_r3"
                  value={results.R3 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_r3")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
            <Col xs={8} sm={8}>
              <FormItem label="X3" style={{ marginBottom: 0 }}>
                <Input
                  id="result_x3"
                  value={results.X3 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_x3")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
            <Col xs={8} sm={8}>
              <FormItem label="Z3" style={{ marginBottom: 0 }}>
                <Input
                  id="result_z3"
                  value={results.Z3 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_z3")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
          </Row>
        )}

        <Row gutter={[12, 12]} style={{ marginBottom: 10 }}>
          <Col xs={8} sm={8}>
            <FormItem label="∠" style={{ marginBottom: 0 }}>
              <Input
                id="result_angle1"
                value={results.angle1 || ''}
                size="large"
                readOnly
                onClick={copy}
                placeholder="计算结果"
                suffix={<IconCopy onClick={copy.bind(this, null, "#result_angle1")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
              />
            </FormItem>
          </Col>
          <Col xs={8} sm={8}>
            <FormItem label="X1" style={{ marginBottom: 0 }}>
              <Input
                id="result_x1"
                value={results.X1 || ''}
                size="large"
                readOnly
                onClick={copy}
                placeholder="计算结果"
                suffix={<IconCopy onClick={copy.bind(this, null, "#result_x1")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
              />
            </FormItem>
          </Col>
          <Col xs={8} sm={8}>
            <FormItem label="Z1" style={{ marginBottom: 0 }}>
              <Input
                id="result_z1"
                value={results.Z1 || ''}
                size="large"
                readOnly
                onClick={copy}
                placeholder="计算结果"
                suffix={<IconCopy onClick={copy.bind(this, null, "#result_z1")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
              />
            </FormItem>
          </Col>
        </Row>

        {calculationType !== "2" && (
          <Row gutter={[12, 12]} style={{ marginBottom: 10 }}>
            <Col xs={8} sm={8}>
              <FormItem label="R" style={{ marginBottom: 0 }}>
                <Input
                  id="result_r2"
                  value={results.R2 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_r2")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
            <Col xs={8} sm={8}>
              <FormItem label="X2" style={{ marginBottom: 0 }}>
                <Input
                  id="result_x2"
                  value={results.X2 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_x2")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
            <Col xs={8} sm={8}>
              <FormItem label="Z2" style={{ marginBottom: 0 }}>
                <Input
                  id="result_z2"
                  value={results.Z2 || ''}
                  size="large"
                  readOnly
                  onClick={copy}
                  placeholder="计算结果"
                  suffix={<IconCopy onClick={copy.bind(this, null, "#result_z2")} className="result-copy-icon" style={{ cursor: "pointer" }} />}
                />
              </FormItem>
            </Col>
          </Row>
        )}
      </div>

      {pointData.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 15, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>点位表</h3>
            <Button
              type="primary"
              size="small"
              icon={<IconImport />}
              onClick={onExportToDresser}
            >
              一键导入到修砂轮程序
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14 }}>从 A</span>
              <InputNumber
                size="small"
                min={1}
                max={20}
                value={exportStartRow}
                onChange={setExportStartRow}
                style={{ width: 60 }}
              />
              <span style={{ fontSize: 14 }}>开始</span>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={columns}
              data={pointData}
              pagination={false}
              border
              scroll={{ x: true }}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '20px 16px' }}>
      <style>{`
        @media (max-width: 768px) {
          .result-copy-icon {
            display: none !important;
          }
        }
      `}</style>
      <div className="cpn-wrapper-vertical" style={{ maxWidth: 800, margin: '0 auto' }}>
        {renderInputForm()}
        <Divider />
        {renderOutputForm()}
      </div>
    </div>
  );
};

export default TangentCalculation;
